import type {RunHangReportInput} from '@cip/code-runner';
import {serverTimestamp} from 'firebase/firestore';
import {getUserId} from 'hooks/user/useUserId';
import {BUILD_COMMIT} from 'utils/general';
import {getOrCreateSessionId} from '@cip/core/utils';
import {
  writeRunHangReport,
  type ClientRunHangReport,
} from '@cip/core/firebase/client';

/** Build the wire payload from the package-level input + ambient browser context. */
function buildRunHangReport(report: RunHangReportInput): ClientRunHangReport {
  return {
    userId: getUserId() ?? null,
    runId: report.runId,
    lastCheckpoint: report.lastCheckpoint,
    msStuckAtCheckpoint: report.msStuckAtCheckpoint,
    awaitingStdin: report.awaitingStdin,
    pendingStdinMessageId: report.pendingStdinMessageId,
    previousRunOutcome: report.previousRunOutcome,
    testMode: report.testMode,
    stepMode: report.stepMode,
    firingIndex: report.firingIndex,
    serviceWorkerControllerScriptUrl:
      navigator.serviceWorker?.controller?.scriptURL ?? null,
    sharedArrayBufferAvailable: typeof SharedArrayBuffer !== 'undefined',
    userAgent: navigator.userAgent,
    url: window.location.href,
    commit: BUILD_COMMIT,
    sessionId: getOrCreateSessionId(),
  };
}

/**
 * Sync-signature adapter for `@cip/code-runner`'s `RunHangReporter`. Schedules
 * the async write without awaiting so a slow Firestore call doesn't delay
 * subsequent watchdog firings. Write errors are debug-logged so a Firestore
 * outage can't itself trigger the global `unhandledrejection` listener.
 *
 * @param report Beacon payload from `@cip/code-runner`.
 */
export function reportRunHang(report: RunHangReportInput): void {
  void writeRunHangReport({
    event: buildRunHangReport(report),
    serverNow: serverTimestamp(),
  }).catch(error => {
    console.debug('[runHangReporter] write failed', error);
  });
}
